/**
 * /best-crm-for-solo-agents — SEO landing page targeting the head-term
 * "best CRM for solo real estate agents". FAQPage schema injected at
 * mount so Google can pull rich results without visiting the SPA.
 */
import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check } from "lucide-react";
import { usePageSeo, usePageSchema } from "../lib/seo";

const FAQ = [
  {
    q: "Why do solo agents pick iKEY over Follow Up Boss or Wise Agent?",
    a: "Both alternatives leave e-signatures, client portals, and deadline tracking outside the tool — so a solo agent ends up paying for two or three subscriptions to close a deal. iKEY bundles DocuSign, a branded client portal, transaction tracking, and CRM into a single plan built for one agent.",
  },
  {
    q: "How much does the best CRM for a solo agent cost?",
    a: "iKEY is free forever for your first three clients (CRM only). When you outgrow that, Growth is $39.99/month with DocuSign included. Every paid tier is 18% cheaper if you pay annually.",
  },
  {
    q: "Do I need to configure workflows or automations to get started?",
    a: "No. iKEY's Run-My-Day screen ranks who to call today based on lead score, activity, and inquiry age. Speed-to-lead is built in — no drip campaigns to design, no automations to wire up.",
  },
  {
    q: "Can I run iKEY from my phone?",
    a: "Yes. iKEY ships a native iOS + Android app (Expo) with the same Run-My-Day, Client 360, and transaction detail screens as the desktop web app. From first showing to closing table, everything runs from your pocket.",
  },
];

const FAQ_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQ.map((it) => ({
    "@type": "Question",
    name: it.q,
    acceptedAnswer: { "@type": "Answer", text: it.a },
  })),
};

export default function BestCrmForSoloAgents() {
  usePageSeo({
    title: "Best CRM for Solo Real Estate Agents (2026) — iKEY",
    description: "Looking for the best CRM for solo real estate agents? iKEY includes DocuSign, a client portal, and AI briefings. Free forever for your first 3 clients.",
    canonical: "https://ikey-app.com/best-crm-for-solo-agents",
  });
  usePageSchema(FAQ_SCHEMA);
  // Legacy in-file setup (title/desc/script) removed — usePageSeo +
  // usePageSchema replace it and handle cleanup on unmount.
  useEffect(() => {}, []);

  return (
    <div className="min-h-screen bg-white text-slate-900" data-testid="best-crm-solo-page">
      <header className="sticky top-0 z-40 bg-white/85 backdrop-blur-md border-b border-slate-200/60">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <img src="/ikey-logo.png" alt="iKEY" className="h-9 w-9 rounded-lg ring-1 ring-slate-200" />
            <span className="font-heading text-lg font-bold tracking-tight">iKEY</span>
          </Link>
          <Link to="/" className="text-sm font-semibold text-slate-600 hover:text-slate-900 inline-flex items-center gap-1.5">
            <ArrowLeft className="h-4 w-4" /> Back to home
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 py-14 lg:py-20">
        <div className="text-[11px] font-semibold uppercase tracking-widest" style={{ color: "var(--primary-hex)" }}>
          Solo Agent Hub
        </div>
        <h1 className="mt-3 font-heading text-4xl sm:text-5xl font-bold tracking-tighter leading-[1.05]">
          The best CRM for solo real estate agents in 2026.
        </h1>
        <p className="mt-5 text-lg text-slate-600 leading-relaxed">
          Most real-estate CRMs are priced and designed for teams — seat licenses, admin roles, brokerage rollups.
          If you close deals on your own, that's dead weight. iKEY is the only workspace built for one agent
          from lead to closing table.
        </p>

        <section className="mt-14 rounded-2xl bg-slate-50 ring-1 ring-slate-200 p-6 sm:p-8">
          <h2 className="font-heading text-2xl font-bold tracking-tight text-slate-900">Why iKEY for Solo</h2>
          <ul className="mt-5 space-y-3">
            {[
              "Free forever for your first three clients — a real CRM, not a 14-day trial that expires with your workflow.",
              "DocuSign built in on every paid plan — no $45–$85/month DocuSign line-item, no juggling tabs.",
              "Speed-to-lead built in — the AI ranks who to call within minutes of a new inquiry, opener already drafted.",
              "Branded client portal — buyers and sellers log into your face, not a generic CRM login.",
              "One agent per plan, up to 20 clients on Master ($79.99/mo) — you outgrow the plan by book size, not by seat count.",
              "Native iOS + Android — from first showing to closing table, everything runs from your pocket.",
              "One-click CSV export on every plan — your book is yours, always.",
            ].map((line) => (
              <li key={line} className="flex items-start gap-3 text-sm text-slate-800 leading-relaxed">
                <Check className="h-4 w-4 mt-0.5 shrink-0 text-emerald-600" />
                <span>{line}</span>
              </li>
            ))}
          </ul>
        </section>

        <section className="mt-14">
          <h2 className="font-heading text-2xl font-bold tracking-tight text-slate-900">Common questions</h2>
          <dl className="mt-6 space-y-3">
            {FAQ.map((it, i) => (
              <div key={i} className="rounded-xl bg-white ring-1 ring-slate-200 p-5">
                <dt className="font-heading font-bold text-slate-900 text-base">{it.q}</dt>
                <dd className="mt-2 text-sm text-slate-700 leading-relaxed">{it.a}</dd>
              </div>
            ))}
          </dl>
        </section>

        <div className="mt-14 flex items-center gap-3 flex-wrap">
          <Link to="/auth" className="inline-flex items-center gap-2 rounded-md px-5 py-3 text-sm font-bold text-white bg-slate-900 hover:bg-slate-800">Try iKEY free <ArrowRight className="h-4 w-4" /></Link>
          <Link to="/pricing" className="inline-flex items-center gap-2 rounded-md px-5 py-3 text-sm font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200">See pricing</Link>
          <Link to="/vs" className="inline-flex items-center gap-2 rounded-md px-5 py-3 text-sm font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200">Compare CRMs</Link>
        </div>
      </main>

      <footer className="border-t border-slate-200 py-10">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 text-center text-sm text-slate-500">
          © {new Date().getFullYear()} iKEY, Inc. · <Link to="/terms" className="hover:text-slate-900">Terms</Link> · <Link to="/privacy" className="hover:text-slate-900">Privacy</Link>
        </div>
      </footer>
    </div>
  );
}
